<template>
  <div class="question-block" :class="{ 'is-saving': isSaving }">
    <div class="question-block__title">
      <drag-title-block
        class="question-block__drag"
        :title="index + 1 + $tr('vacancy.tests.form.questions.question.number')"
      />

      <div
        class="question-block__collapse"
        :class="{ 'is-collapsed': isCollapsed }"
        @click="isCollapsed = !isCollapsed"
      >
        <i class="icon icon-caret"></i>
      </div>

      <div v-if="!isDeleteDisabled" class="question-block__delete" @click="DEL_QUESTION(item._id)">
        <i class="icon icon-recycle_bin"></i>
      </div>
    </div>

    <div class="question" :class="{ 'is-collapsed': isCollapsed }">
      <div class="question-block__question-body">
        <div class="question-block__question-body-top">
          <multiselect
            v-model="selectedType"
            open-direction="bottom"
            track-by="type"
            label="name"
            :options="types"
            :show-labels="false"
            :searchable="false"
            icon_select
            @input="setQuestionProperty({ property: 'type', newValue: selectedType.id })"
          />

          <div v-if="item.type !== 'file'" class="question-block__question-body-top-switches">
            <trigger
              :value="item.is_variables_enabled"
              small
              :text="$tr('question.question-with-formulas')"
              @input="setQuestionProperty({ property: 'is_variables_enabled', newValue: !item.is_variables_enabled })"
            />

            <trigger
              :value="item.is_graph_enabled && selectedType.type !== 'file'"
              small
              :text="$tr('question.question-with-charts')"
              @input="setQuestionProperty({ property: 'is_graph_enabled', newValue: !item.is_graph_enabled })"
            />
          </div>
        </div>

        <div class="veditor-block">
          <div v-if="item.is_graph_enabled && selectedType.type !== 'file'" class="question-block__charts">
            <div class="question-block__chart-tabs">
              <div
                v-for="(config, indexChart) in getQuestionCharts(item._id)"
                :key="indexChart"
                class="question-block__chart-tab"
                :class="{ active: activeChartTab === indexChart }"
              >
                <div class="question-block__chart-tab-name" @click="activeChartTab = indexChart">
                  <div class="question-block__chart-tab-button">
                    <span>{{ configTypeComputed(config) }}</span>
                    <i
                      class="icon icon-caret"
                      :class="{ active: isMenuTypeGraphsOpened === indexChart }"
                      @click="toggleTypeGraphMenu(indexChart)"
                    ></i>
                  </div>

                  <transition name="slide-fade">
                    <div v-if="isMenuTypeGraphsOpened === indexChart" class="question-block__chart-tab-types">
                      <div
                        v-for="typeGraph in typesGraph"
                        :key="typeGraph.type"
                        class="question-block__chart-tab-type"
                        :class="{ active: config.type === typeGraph.type }"
                        @click="changeChartType({ indexChart, newType: typeGraph.type })"
                      >
                        {{ typeGraph.name }}
                      </div>
                    </div>
                  </transition>
                </div>

                <div
                  class="question-block__chart-tab-close"
                  @click="removeChartConfig({ questionId: item._id, indexChart })"
                >
                  <i class="icon icon-close"></i>
                </div>
              </div>

              <div
                v-if="getQuestionCharts(item._id).length < 6"
                class="question-block__chart-tab"
                @click="addChartConfig"
              >
                <div class="question-block__chart-tab-name add">+</div>
              </div>
            </div>

            <div v-if="getQuestionCharts(item._id).length > 0" class="question-block__charts-content">
              <transition-group name="fade" mode="out-in">
                <div
                  v-for="(config, indexChart) in getQuestionCharts(item._id)"
                  v-show="activeChartTab === indexChart"
                  :key="`${indexChart}${Math.random()}`"
                  class="question-block__charts-content-item"
                >
                  <vacancy-test-question-chart
                    :question-id="item._id"
                    :index-chart="indexChart"
                    :chart-config="config"
                    :question-variables="item.variables"
                  />
                </div>
              </transition-group>
            </div>
          </div>

          <vacancy-test-question-variables
            v-if="item.is_variables_enabled"
            :variables="item.variables"
            :question-id="item._id"
            :rounding-precision="item.rounding_precision"
          />

          <vcv-tiptap
            :value="item.body"
            :variables="variablesForEditor"
            :is-empty="item.body === '' || item.body === '<p></p>'"
            @input="setQuestionProperty({ property: 'body', newValue: $event })"
          >
            <template slot="toolbar">
              <button
                v-tooltip.top-center="$tr('editor.uploadAudioTooltip')"
                class="vcv-tiptap-menu-item__item ql-audio"
                :class="{ 'is-disabled': hasImage || has_video }"
              >
                <input
                  ref="audio"
                  class="ql-audio-file"
                  :disabled="hasImage || has_video"
                  type="file"
                  accept=".mp3,audio/*"
                  @change="change_audio"
                />

                <i class="icon-soundcloud-line"></i>
              </button>

              <button
                v-tooltip.top-center="$tr('editor.add-youtube-video')"
                class="vcv-tiptap-menu-item__item"
                title="Youtube"
                :class="{ 'is-disabled': hasImage || has_audio }"
                @click="youtubeToggle"
              >
                <i class="icon-youtube-line"></i>
              </button>
            </template>
          </vcv-tiptap>

          <div v-if="isShowYoutubeForm && !hasImage && !has_audio" class="video-container-wrap">
            <video-container
              :placeholder="$tr('vacancy.media.youtube_link')"
              :value="youtube_link"
              :title="video_title"
              :is-read-only="false"
              @setVideoLink="setVideoLink"
              @change_duration="changeQuestionTimelimit"
              @set_title="setVideoTitle"
              @show_modal="isShowVideoModal = true"
            />
          </div>

          <div v-if="audio && !hasImage && !has_video" class="audio-block">
            <vaudio class="audio" :src="audio.src" :name="audio.name" @change_duration="changeQuestionTimelimit" />

            <div class="audio-remove" @click="remove_audio">
              <img src="/static/icons/video-close.png" />
            </div>
          </div>

          <div
            v-if="(audio || youtube_link) && !isPageSingle"
            class="set_separate_page"
            :class="{ mrb0: !item.has_valid_answer }"
          >
            <vchecklabel
              :label="$tr('vacancy.media.separate_page')"
              :value="is_separate_page"
              @input="setSeparatePage"
            />
          </div>
        </div>
      </div>

      <div class="question_answers">
        <div class="check-row" :class="{ 'is-column': item.type === 'value' }">
          <div class="row-block">
            <div v-if="isShowTimer" class="timer-block">
              <div class="timer-block-content">
                <div class="timer-block-note">
                  {{ $tr('vacancy.videoInterview.timeForAnswer') }}
                </div>

                <div class="test-time-wrapper">
                  <masked-input
                    v-model="timeLimitNew.h"
                    class="input_block"
                    :class="{
                      error:
                        (isTimerLessDuration && item.time_limit_enabled) || (!isTimeValid && item.time_limit_enabled)
                    }"
                    type="text"
                    placeholder="00"
                    :mask="[/[0-9]/, /[0-9]/]"
                    keep-char-positions
                    :disabled="!item.time_limit_enabled"
                    @input="setTimeLimit('h', $event)"
                  />

                  <span>:</span>

                  <masked-input
                    v-model="timeLimitNew.m"
                    class="input_block"
                    :class="{
                      error:
                        (isTimerLessDuration && item.time_limit_enabled) || (!isTimeValid && item.time_limit_enabled)
                    }"
                    type="text"
                    placeholder="00"
                    :mask="[/[0-5]/, /[0-9]/]"
                    keep-char-positions
                    :disabled="!item.time_limit_enabled"
                    @input="setTimeLimit('m', $event)"
                  />

                  <span>:</span>

                  <masked-input
                    v-model="timeLimitNew.s"
                    class="input_block"
                    :class="{
                      error:
                        (isTimerLessDuration && item.time_limit_enabled) || (!isTimeValid && item.time_limit_enabled)
                    }"
                    type="text"
                    placeholder="00"
                    :mask="[/[0-5]/, /[0-9]/]"
                    keep-char-positions
                    :disabled="!item.time_limit_enabled"
                    @input="setTimeLimit('s', $event)"
                  />
                </div>
              </div>

              <div
                v-if="isShowTimer && isTimerLessDuration"
                class="error time-error"
                v-html="$tr('vacancy.tests.media.time_less_duration', { minutes: Math.ceil(item.duration / 60) })"
              ></div>
            </div>

            <div v-on-clickaway="hideOptions" class="options-dropdown">
              <div
                class="options-label"
                :class="{ 'is-active': isOptionsVisible }"
                @click="isOptionsVisible = !isOptionsVisible"
              >
                {{ $tr('responses.resume.questionnaire.additional') }}
                <img src="/static/images/elements/module-arrow-right.svg" />
              </div>

              <transition name="modal">
                <div v-if="isOptionsVisible" class="options-select options">
                  <div v-if="isShowTimer" class="options-select-option">
                    <vchecklabel
                      :label="$tr('vacancy.tests.form.question.time_limit_enabled.checkbox')"
                      :tooltip="{
                        content: $tr('vacancy.tests.form.questions.timer.checkbox.tooltip'),
                        placement: 'auto',
                        classes: 'tight'
                      }"
                      :value="item.time_limit_enabled"
                      @input="
                        setQuestionProperty({ property: 'time_limit_enabled', newValue: !item.time_limit_enabled })
                      "
                    />
                  </div>

                  <div v-if="item.type !== 'value' && item.type !== 'file'" class="options-select-option">
                    <vchecklabel
                      :label="$tr('vacancy.tests.form.question.noAnswer')"
                      :value="!item.has_valid_answer"
                      @input="setQuestionProperty({ property: 'has_valid_answer', newValue: !item.has_valid_answer })"
                    />
                  </div>

                  <div v-if="item.type === 'value'" class="options-select-option">
                    <vchecklabel
                      :label="$tr('vacancy.tests.form.question.has_valid_answer')"
                      :value="item.has_valid_answer"
                      :tooltip="{
                        content: $tr('vacancy.tests.form.questions.answer.value.checkbox.tooltip'),
                        placement: 'auto',
                        classes: 'tight'
                      }"
                      @input="setQuestionProperty({ property: 'has_valid_answer', newValue: !item.has_valid_answer })"
                    />
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>

        <div
          v-if="
            item.type === 'choice' || item.type === 'multichoice' || (item.type === 'value' && item.has_valid_answer)
          "
          class="answers"
        >
          <container group-name="answers" lock-axis="y" drag-handle-selector=".answer__drag" @drop="answerOnDrop">
            <draggable v-for="obj in item.answers" :key="obj._id" :index="obj._id">
              <div class="answer" :class="{ error: obj.body === '' }">
                <div v-if="item.answers.length > 1 && isAnswersSortEnabled" class="answer__drag">
                  <div class="answer__drag-dots">
                    <div class="answer__drag-dot"></div>
                    <div class="answer__drag-dot"></div>
                    <div class="answer__drag-dot"></div>
                  </div>
                </div>

                <div v-if="item.type !== 'value'" :class="['controls', { disabled: !item.has_valid_answer }]">
                  <vfocus-field>
                    <vcheckbox
                      v-if="item.type === 'multichoice'"
                      :value="obj.valid"
                      :disabled="!item.has_valid_answer"
                      :v-tooltip="{
                        content: $tr('vacancy.tests.form.questions.answer.other.checkbox.tooltip'),
                        placement: 'auto',
                        classes: 'tight'
                      }"
                      @input="
                        SET_ANSWER_PROPERTY({
                          questionId: item._id,
                          answerId: obj._id,
                          property: 'valid',
                          newValue: $event
                        })
                      "
                    />

                    <vcircle
                      v-if="item.type === 'choice'"
                      :value="obj.valid"
                      :v-tooltip="{
                        content: $tr('vacancy.tests.form.questions.answer.other.radio.tooltip'),
                        placement: 'auto',
                        classes: 'tight'
                      }"
                      @input="
                        SET_ANSWER_PROPERTY({
                          questionId: item._id,
                          answerId: obj._id,
                          property: 'valid',
                          newValue: $event
                        })
                      "
                    />
                  </vfocus-field>
                </div>

                <trigger
                  v-if="item.is_variables_enabled && item.type !== 'value'"
                  v-tooltip.top-center="'Ответ включает формулу'"
                  class="answers__trigger"
                  :value="obj.is_formula"
                  small
                  @input="
                    SET_ANSWER_PROPERTY({
                      questionId: item._id,
                      answerId: obj._id,
                      property: 'is_formula',
                      newValue: $event
                    })
                  "
                />

                <vtextarea
                  v-if="item.has_valid_answer || item.type !== 'value'"
                  :id="`input-option-${obj._id}`"
                  class="page_tests_input"
                  :class="{ val: item.type === 'value', error: obj.body === '' }"
                  :value="obj.body"
                  auto_height
                  resizable-y
                  :min_height="40"
                  :placeholder="$tr('vacancy.tests.form.questions.answers.value.input')"
                  :focus="answer_focus"
                  :error="answerError(obj.body)"
                  @input="
                    SET_ANSWER_PROPERTY({ questionId: item._id, answerId: obj._id, property: 'body', newValue: $event })
                  "
                />

                <vfocus-field v-if="item.answers.length > 1">
                  <div
                    class="question-block__answer-delete"
                    @click="DEL_ANSWER({ questionId: item._id, answerId: obj._id })"
                  >
                    <i class="icon icon-recycle_bin"></i>
                  </div>
                </vfocus-field>
              </div>
            </draggable>
          </container>

          <div v-if="item.type !== 'value'" class="answer">
            <div class="controls disabled">
              <vfocus-field>
                <vcheckbox v-if="item.type === 'multichoice'" disabled />

                <vcircle v-if="item.type === 'choice'" disabled />
              </vfocus-field>
            </div>

            <div
              class="page_tests_input page_tests_input__other"
              :class="{ 'page_tests_input__other-value': item.type === 'value' }"
            >
              <div class="other__add-type other__type" @click="addAnswer">
                {{ $tr('vacancy.tests.form.button.add_answer') }}
              </div>
            </div>

            <div class="question-block__answer-delete"></div>
          </div>
        </div>

        <div v-if="item.type === 'file'" class="answers__type answers__type_file">
          <label class="file-title">{{ $tr('vacancy.preferences.fileUploadLabel') }}</label>

          <div class="file-wrapper">
            <span>{{ $tr('tests.uploadFile') }}</span>

            <div class="actions">
              <icon-upload width="31px" height="20px" />
            </div>
          </div>
        </div>
      </div>

      <modal v-if="isShowVideoModal" redesign :has_footer="false" @close="isShowVideoModal = false">
        <div slot="header">{{ video_title || '' }}</div>

        <div slot="body">
          <div v-if="youtube_link" class="modal-video">
            <vrecorder class="question_video" :src="youtube_link" :show_recorder="false" show_player is_youtube />
          </div>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import VideoContainer from '@/components/shared/VideoContainer.vue';
import Modal from '@/components/Modal.vue';
import MaskedInput from 'vue-text-mask';
import DragTitleBlock from '@/routes_new/inner/vacancy/components/DragTitleBlock';
import VacancyTestQuestionChart from '@/routes_new/inner/vacancy/test/VacancyTestQuestionChart';
import VacancyTestQuestionVariables from '@/routes_new/inner/vacancy/test/VacancyTestQuestionVariables';
import { Container, Draggable } from 'vue-smooth-dnd';
import Trigger from '@/components/shared/Trigger';
import VcvTiptap from '@/components/VcvTiptap/VcvTiptap';

export default {
  name: 'VacancyTestQuestion',

  components: {
    VideoContainer,
    Modal,
    MaskedInput,
    DragTitleBlock,
    VacancyTestQuestionChart,
    VacancyTestQuestionVariables,
    Trigger,
    VcvTiptap,
    Container,
    Draggable
  },

  mixins: [clickaway],

  // props: ['check_errors'],

  props: {
    item: {
      type: Object,
      default: () => {}
    },

    index: {
      type: Number,
      default: 0
    },

    isDeleteDisabled: {
      type: Boolean,
      default: false
    },

    isShowTimer: {
      type: Boolean,
      default: false
    },

    isSaving: {
      type: Boolean,
      default: false
    },

    isPageSingle: {
      type: Boolean,
      default: false
    },

    isAnswersSortEnabled: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      types: [
        {
          id: 'choice',
          type: 'choice',
          name: this.$tr('vacancy.tests.form.questions.types.choice')
        },
        {
          id: 'multichoice',
          type: 'multichoice',
          name: this.$tr('vacancy.tests.form.questions.types.multichoice')
        },
        {
          id: 'value',
          type: 'value',
          name: this.$tr('vacancy.tests.form.questions.types.value')
        },
        { id: 'file', type: 'file', name: this.$tr('vacancy.tests.form.questions.types.file') }
      ],

      type: null,
      answer_focus: false,
      youtube_link: null,
      video_title: '',
      isShowYoutubeForm: false,
      file: null,
      audio: null,
      audio_file: null,
      media: null,
      is_separate_page: false,
      isShowVideoModal: false,
      audio_size: 0,
      isOptionsVisible: false,
      timeRegex: /[0-9][0-9]:[0-5][0-9]/g,
      timeLimitNew: { h: '00', m: '00', s: '00' },

      // charts/graphs
      typesGraph: [
        { type: 'line', name: this.$tr('graph.line') },
        { type: 'area', name: this.$tr('graph.area') },
        { type: 'pie', name: this.$tr('graph.pie') },
        { type: 'donut', name: this.$tr('graph.donut') },
        { type: 'radar', name: this.$tr('graph.radar') },
        { type: 'bar', name: this.$tr('graph.bar') },
        { type: 'combo', name: this.$tr('graph.combo') }
      ],

      isCollapsed: false,
      activeChartTab: 0,
      isMenuTypeGraphsOpened: null // 0 - closed all, 1-x = index of opened menu
    };
  },

  computed: {
    ...mapGetters('vacancyTests', ['getQuestionCharts', 'getApexOptions']),

    ...mapGetters('questions', ['currentGraphType']),

    isTimeValid() {
      if (!this.item.time_limit) {
        return false;
      }

      const fullTime = this.$moment.duration(this.item.time_limit, 'seconds').format('HH:mm:ss');

      return !!fullTime.match(this.timeRegex) && !!fullTime.match(this.timeRegex).length;
    },

    isTimerLessDuration() {
      if (!this.isShowTimer || !this.item.duration || !this.item.time_limit_enabled) {
        return false;
      }

      return this.item.time_limit < Math.ceil(this.item.duration / 60);
    },

    selectedType: {
      get() {
        if (this.type === null) {
          return this.types.find(item => item.id === this.item.type);
        }

        return this.type;
      },

      set(val) {
        this.type = val;
      }
    },

    has_audio() {
      return !!this.audio;
    },

    has_video() {
      return !!this.youtube_link;
    },

    hasImage() {
      if (!this.item.body) return false;

      return this.item.body.indexOf('img src=') !== -1;
    },

    variablesForEditor() {
      return this.item.variables.map(x => ({ id: x.name, name: x.name }));
    }
  },

  watch: {
    youtube_link(val) {
      if (val) {
        this.media = {
          url: val,
          type: 'youtube',
          title: this.video_title || '',
          is_separate_page: this.is_separate_page,
          vacancy_module_test_question_id: this.item._id
        };
      } else {
        this.media = null;
        this.changeQuestionTimelimit(0);
        this.is_separate_page = false;
      }
      this.SET_QUESTION_MEDIA({ questionId: this.item._id, media: this.media });
    },

    audio(val) {
      if (val) {
        if (this.audio_file) {
          this.$store
            .dispatch('services/saveCompanyImage', { imageUrl: this.audio_file })
            .then(data => {
              this.media = {
                url: data.url,
                type: 'audio',
                title: val.name,
                is_separate_page: this.is_separate_page,
                vacancy_module_test_question_id: this.item._id
              };
              this.SET_QUESTION_MEDIA({ questionId: this.item._id, media: this.media });
            })
            .catch(err => {
              if (err && err.response && err.response.data && err.response.data.meta) {
                this.$noty.error(err.response.data.meta.error.message);
              }
              this.remove_audio();
            });
        } else {
          this.media = {
            url: val.src,
            type: 'audio',
            title: val.name,
            is_separate_page: this.is_separate_page,
            vacancy_module_test_question_id: this.item._id
          };
          this.SET_QUESTION_MEDIA({ questionId: this.item._id, media: this.media });
        }
      } else {
        this.media = null;
        this.is_separate_page = false;
        this.changeQuestionTimelimit(0);
        this.SET_QUESTION_MEDIA({ questionId: this.item._id, media: this.media });
      }
    }
  },

  mounted() {
    if (this.item.time_limit === 0) {
      this.timeLimitNew.m = '02';
    }

    if (this.item.time_limit > 86400) {
      const duration = this.$moment.duration(this.item.time_limit, 'seconds');
      const time = duration.format('HH:mm:ss').split(':');

      this.timeLimitNew.h = time[0];
      this.timeLimitNew.m = time[1];
      this.timeLimitNew.s = time[2];
    } else {
      const time = this.$moment
        .utc(this.item.time_limit * 1000)
        .format('HH:mm:ss')
        .split(':');

      this.timeLimitNew.h = time[0];
      this.timeLimitNew.m = time[1];
      this.timeLimitNew.s = time[2];
    }

    if (this.item && this.item.media && this.item.media !== null) {
      this.media = Object.assign({}, this.media, {
        ...this.item.media,
        vacancy_module_test_question_id: this.item._id
      });

      // this.SET_VACANCY_MODULE_TEST_QUESTION_ID({ questionId: this.item._id, value: this.item._id });

      if (this.item.media.type === 'audio') {
        this.audio = {
          src: this.item.media.url,
          name: this.item.media.title
        };
      }

      if (this.item.media.type === 'youtube') {
        this.isShowYoutubeForm = true;
        this.setVideoLink(this.item.media.url);
      }

      if (this.item.media.is_separate_page !== undefined) {
        this.setSeparatePage(this.item.media.is_separate_page);
      }
    }
  },

  methods: {
    ...mapMutations('vacancyTests', [
      'SET_QUESTION_PROPERTY',
      'SET_QUESTION_ANSWERS',
      'DEL_QUESTION',
      'CHANGE_CHART_TYPE',
      'ADD_ANSWER',
      'SET_ANSWER_PROPERTY',
      'DEL_ANSWER',
      'ADD_EMPTY_CHARTCONFIG',
      'REMOVE_CHARTCONFIG',
      'SET_QUESTION_MEDIA',
      'SET_QUESTION_MEDIA_IS_SEPARATE_PAGE',
      'SET_QUESTION_MEDIA_TITLE',
      'SET_VACANCY_MODULE_TEST_QUESTION_ID'
    ]),

    ...mapMutations('questions', ['toggleVariablesEnabled', 'toggleGraphEnabled', 'changeAnswerProperty']),

    answerOnDrop(dragResult) {
      const { removedIndex, addedIndex, payload } = dragResult;
      if (removedIndex === null && addedIndex === null) return this.item.answers;

      const result = [...this.item.answers];
      let itemToAdd = payload;

      if (removedIndex !== null) {
        itemToAdd = result.splice(removedIndex, 1)[0];
      }

      if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd);
      }

      this.SET_QUESTION_ANSWERS({ questionId: this.item._id, newAnswers: result });
    },

    setQuestionProperty({ property, newValue }) {
      this.SET_QUESTION_PROPERTY({ questionId: this.item._id, property, newValue });
    },

    setTimeLimit(unit, value) {
      this.timeLimitNew[unit] = value;
      const newValue = this.$moment
        .duration(`${this.timeLimitNew.h}:${this.timeLimitNew.m}:${this.timeLimitNew.s}`)
        .asSeconds();

      this.SET_QUESTION_PROPERTY({ questionId: this.item._id, property: 'time_limit', newValue });
    },

    answerError(v) {
      if (v === '' && this.check_errors) {
        return this.$tr('common.requiredField');
      } else {
        return '';
      }
    },

    addAnswer() {
      if (this.item.type === 'choice' || this.item.type === 'multichoice') {
        this.ADD_ANSWER({ questionId: this.item._id });
      }
    },

    addChartConfig() {
      this.ADD_EMPTY_CHARTCONFIG({ questionId: this.item._id });
      this.activeChartTab = this.item.chart_config !== null ? this.item.chart_config.length - 1 : 0;
    },

    removeChartConfig({ questionId, indexChart }) {
      this.REMOVE_CHARTCONFIG({ questionId, indexChart });
      this.activeChartTab = this.item.chart_config.length - 1;
    },

    toggleTypeGraphMenu(indexChart) {
      if (this.isMenuTypeGraphsOpened === null) {
        this.isMenuTypeGraphsOpened = indexChart;
      } else {
        this.isMenuTypeGraphsOpened = null;
      }
    },

    configTypeComputed(config) {
      const isComboSeries = config.series.some(x => x.type !== undefined && x.type !== null);

      if (config.type === 'line' && isComboSeries) {
        const comboIndex = this.typesGraph.findIndex(x => x.type === 'combo');
        return this.typesGraph[comboIndex].name;
      } else {
        const nameIndex = this.typesGraph.findIndex(x => x.type === config.type);
        return this.typesGraph[nameIndex].name;
      }
    },

    changeChartType({ indexChart, newType }) {
      let currentType = 'line';

      if (this.item.chart_config !== null && this.item.chart_config[indexChart] !== undefined) {
        currentType = this.item.chart_config[indexChart].type;
      }

      if (newType === currentType) return;

      const c = confirm('При смене типа графика данные будут перезаписаны. Продолжить?');
      if (!c) return;

      this.CHANGE_CHART_TYPE({ questionId: this.item._id, indexChart, newType });
      this.isMenuTypeGraphsOpened = null;
    },

    hideOptions() {
      this.isOptionsVisible = false;
    },

    youtubeToggle() {
      if (this.has_audio || this.hasImage) {
        this.isShowYoutubeForm = false;

        return;
      }

      this.isShowYoutubeForm = !this.isShowYoutubeForm;

      if (!this.isShowYoutubeForm) {
        // this.SET_QUESTION_MEDIA({ questionId: this.item._id, media: null });
        this.setVideoLink('');
      }
    },

    changeQuestionTimelimit(value) {
      console.log(value);
    },

    setVideoTitle(value) {
      this.video_title = value;
      this.media = Object.assign({}, this.media, { title: this.video_title || '' });

      if (this.item.media !== undefined && this.item.media !== null) {
        this.SET_QUESTION_MEDIA_TITLE({ questionId: this.item._id, value });
      }
    },

    remove_audio() {
      this.audio = null;
      const input = this.$refs.audio;
      input.type = 'text';
      input.type = 'file';
      this.SET_QUESTION_MEDIA({ questionId: this.item._id, media: null });
    },

    change_audio() {
      if (this.has_video || this.hasImage) {
        this.remove_audio();
        return;
      }
      this.isShowYoutubeForm = false;
      const files = this.$refs.audio.files;
      const file = files[0];
      this.audio_file = files[0];
      this.audio_size = this.audio_file.size ? this.audio_file.size / 1024 / 1024 : 0;
      if (this.audio_size > 10) {
        this.$noty.error(this.$tr('interview.tests.media.audio_limit'));
      }

      this.audio = {
        src: URL.createObjectURL(file),
        name: file.name
      };
    },

    setVideoLink(val) {
      this.youtube_link = val;
    },

    // setFocusOnLastOption() {
    //   this.$nextTick().then(() => {
    //     document.getElementById(`input-option-${this.answers[this.answers.length - 1]._id}`).focus();
    //   });
    // },

    // optionListener(e, indexAnswer) {
    //   const value = e.target.value;

    //   if (this.answers.length - 1 !== indexAnswer) return;

    //   switch (e.keyCode) {
    //     case 13: // Enter
    //       this.add_answer();
    //       this.setFocusOnLastOption();
    //       break;
    //     case 8: // backspace
    //     case 46: // delete
    //       if (this.answers.length < 2 || value.length !== 0) break;
    //       this.del_answer(this.answers[indexAnswer]._id);
    //       this.setFocusOnLastOption();
    //       break;
    //   }
    // },

    setSeparatePage(value) {
      this.is_separate_page = value;

      this.SET_QUESTION_MEDIA_IS_SEPARATE_PAGE({ questionId: this.item._id, value });

      if (this.media) {
        this.media = Object.assign({}, this.media, { is_separate_page: value });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

::v-deep .quillWrapper .ql-snow.ql-toolbar {
  display: flex;
  align-items: center;
}

::v-deep .textarea {
  width: 100%;
  max-height: 130px;
}

.question-block {
  position: relative;
  box-shadow: $box-shadow;
  margin-bottom: 20px;
  border-radius: 4px;
  border: 1px solid $grey-light;

  &.is-saving {
    opacity: 0.7;
    pointer-events: none;
  }

  &__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid $grey-light;
  }

  &__drag {
    flex: 1;
  }

  &__answer-options {
    > * {
      margin-bottom: 3px;
    }
  }

  &__collapse {
    width: 47px;
    height: 47px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 18px;
    color: $grey;
    background: $background;
    transition: all ease-in-out 0.3s;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:hover {
      background: $blue-light;
      color: $white;
      transition: all 0.3s;
    }

    &.is-collapsed {
      .icon {
        transform: rotate(180deg);
        transition: transform 0.2s ease-in-out;
      }
    }

    .icon {
      transition: transform 0.2s ease-in-out;
    }
  }

  &__delete {
    width: 47px;
    height: 47px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 24px;
    color: $red;
    background: $background;
    transition: all ease-in-out 0.3s;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:hover {
      background: $red-light;
      color: $white;
      transition: all 0.3s;
    }
  }

  &__question-body {
    padding: 15px 20px;
  }

  &__question-body-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    .multiselect {
      max-width: 270px !important;
    }
  }

  &__question-body-top-switches {
    display: flex;
    flex-direction: column;
  }

  &__chart-tabs {
    display: flex;
    margin-bottom: 15px;
    flex-wrap: wrap;
  }

  &__chart-tab-name {
    @include mixin-text-main;
    color: $grey;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    height: 100%;
    text-transform: capitalize;
    transition: background 0.3s;

    &:hover {
      background: $grey-lighter;
      transition: background 0.3s;
    }

    &.add {
      padding: 0 12px;
    }
  }

  &__chart-tab-button {
    display: flex;
    align-items: center;
    padding: 0 10px;
    height: 100%;

    span {
      margin-right: 10px;
    }

    i {
      transform: rotate(0deg);
      transition: transform 0.2s;
    }

    i.active {
      transform: rotate(180deg);
      transition: transform 0.2s;
    }
  }

  &__chart-tab-types {
    position: absolute;
    top: 30px;
    left: 0;
    padding: 5px 0;
    background: $white;
    box-shadow: $box-shadow;
    border-radius: 4px;
    z-index: 10;
  }

  &__chart-tab-type {
    @include mixin-text-main;
    padding: 5px 20px;
    transition: all 0.3s;

    &.active {
      background: $blue-light-dark;
      color: $white;
    }

    &:hover {
      background: lighten($blue-light, 10%);
      color: $white;
      transition: all 0.3s;
    }
  }

  &__chart-tab-close {
    width: 28px;
    height: 28px;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    i {
      color: $grey;
      font-size: 12px;
    }

    &:hover {
      background: $red-lighter;
      transition: all 0.3s;

      i {
        color: $white;
      }
    }
  }

  &__chart-tab {
    border: 1px solid $grey-light;
    border-radius: 4px;
    background: $white;
    margin-right: 5px;
    height: 30px;
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-bottom: 7px;

    &.active {
      border: 1px solid $blue-light-dark;

      .question-block__chart-tab-name {
        background: $blue-light;
        color: $white;
        transition: background 0.3s;
      }
    }
  }

  ::v-deep {
    .page_tests_input {
      flex: 1;
      margin-top: 0;

      &.val {
        margin-left: 0px;

        &.vinput .input {
          background: #ffffff;
          border: 1px solid #d2d6e0;
          border-radius: 2px;
        }
      }

      .textarea {
        min-height: 50px;
        max-height: 130px;
        transition: border 0.3s;

        &:focus {
          border: 1px solid $green;
          transition: border 0.3s;
        }
      }
    }
  }
  .page_tests_input__other {
    width: 100%;
    padding: 10px 12px;
    border-radius: 0;
    border-bottom: 1px solid #d2d6e0;
    min-height: 40px;
    max-height: 40px;
    box-sizing: border-box;
    color: #9faab6;

    &.page_tests_input__other-value {
      border-bottom: 0;
    }
  }

  &__answer-delete {
    font-size: 20px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    i {
      color: $grey;
      transition: color 0.3s;
    }

    &:hover i {
      color: $red;
      transition: color 0.3s;
    }
  }
}

.question {
  margin-left: 0;
  position: relative;
  background: $white;
  max-height: 10000px;
  transition: max-height 0.2s ease;

  &.is-collapsed {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.2s ease;
  }

  .question_content {
    display: flex;
    align-items: flex-start;

    .error_edit {
      border: 1px solid #f65046;
    }
    .veditor-block {
      flex: 1;
      max-width: 100%;
      margin-bottom: 8px;
    }
  }

  .question_answers {
    margin-left: 0;
    padding: 0 20px 20px;
  }

  .question-title {
    margin-right: 28px;
  }
  .check-row {
    display: flex;
    align-items: center;
    margin-bottom: 6px;

    &.is-column {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  .check-block {
    margin-bottom: 12px;
  }

  &-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .title {
    font-style: normal;
    font-weight: 300;
    font-size: 15px;
    letter-spacing: 0.4px;
    color: rgba(33, 51, 71, 0.8);
    margin-bottom: 10px;
  }
  .margin_b {
    margin-bottom: 35px;
  }
  .maxwid_wrapper {
    margin-top: 20px;
  }
  .maxwid {
    max-width: 215px;
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .answers {
    .answer {
      position: relative;
      display: flex;
      align-items: center;
      padding: 10px 0;

      &__drag {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        width: 20px;
        height: 30px;

        &:hover,
        &:focus,
        &:active {
          .answer__drag-dot {
            background: $grey;
            transition: background 0.3s;
          }
        }
      }

      &__drag-dots {
        text-align: center;
        display: block;
        margin-right: 10px;
      }

      &__drag-dot {
        width: 4px;
        height: 4px;
        background: $grey-light;
        border-radius: 50%;
        margin: 3px 2px;
        display: block;
        transition: background 0.3s;
      }

      &:last-child {
        margin-bottom: 0;
      }

      .controls {
        display: flex;
        align-items: center;
        min-width: 18px;
        max-width: 18px;
        justify-content: center;
        margin-bottom: 0;
        margin-top: 0;
        margin-right: 17px;
        min-height: 18px;
        max-height: 18px;
        position: relative;

        &:hover {
          background-color: rgba(244, 245, 247, 0.56);
        }

        &.disabled {
          opacity: 0.2;

          &:before {
            position: absolute;
            top: 0;
            left: 0;
            content: '';
            width: 100%;
            height: 100%;
            background: transparent;
            z-index: 99;
            cursor: not-allowed;
          }
        }
      }
      ::v-deep {
        .vinput:not(.val) {
          .input {
            padding: 10px 6px;
          }
        }
      }
    }
  }
  .answer {
    &_delete {
      width: 40px;
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      color: $grey-darker;
      transition: color 0.3s;

      &:hover {
        color: $green-hover;
        transition: color 0.3s;
      }
    }
  }

  .use_true_answer {
    display: flex;
    align-items: center;
    .checkbox_label {
      margin-left: 20px;
    }
  }
  .set_separate_page {
    display: flex;
    align-items: center;
    margin-top: 18px;
    .checkbox_label {
      margin-left: 20px;
    }
  }
  .mrb0 {
    margin-bottom: 0;
  }
  .small_input {
    max-width: 30px;
    min-width: 30px;
    margin-left: 2px;
    margin-right: 12px;

    ::v-deep {
      .input {
        padding: 1px 0px;
        min-height: 20px;
        max-height: 20px;
        width: 30px;
        min-width: auto;
      }
    }
  }
  .checkboxer {
    display: flex;
    align-items: center;
    margin-top: 35px;

    &.no-margin-top {
      margin: 0;
    }
  }
}

.test-time-wrapper {
  display: flex;
  align-items: center;

  .input_block {
    @include mixin-text-main;
    color: $black;
    border: 1px solid $grey-light;
    width: 45px !important;
    text-align: center;
    margin: 0 8px !important;
    transition: all 0.3s;

    &:focus {
      border: 1px solid $green-hover;
      transition: all 0.3s;
    }
  }
}

.audio {
  margin-top: 20px;
  margin-bottom: 15px;
  flex: 1;
}

.audio-block {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.audio-remove {
  cursor: pointer;
  margin-left: 10px;
}

.ql-audio-btn {
  cursor: pointer;
}

.ql-upload-btn {
  width: 18px;
  height: 18px;
}

.ql-audio {
  position: relative;
  position: relative;
  cursor: pointer;
  vertical-align: middle;

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.5;

    .ql-audio-btn {
      cursor: not-allowed;
    }

    * {
      cursor: not-allowed;
    }
  }
}

.ql-youtube {
  cursor: pointer;
  width: 17px !important;
  height: 16px !important;
  position: relative;
  padding: 0 !important;
  display: inline-block;
  vertical-align: middle;
  margin: 3px 6px;
}

.ql-image,
.ql-youtube {
  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.image-disabled-icon {
  cursor: not-allowed;
  opacity: 0.5;
  width: 22px !important;
  height: 16px !important;
  position: relative;
  padding: 0 !important;
  display: inline-block;
}

.ql-audio-file {
  opacity: 0;
  position: absolute;
  width: 100%;
  height: 100% !important;
  left: 0;
  top: 0;
  cursor: pointer;
}

.customToolbar {
  display: flex;
  align-items: center;
}

.video-container-wrap {
  margin-top: 20px;
}
.modal-video {
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 -90px 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-error {
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 19px;
  letter-spacing: 0.005em;
  color: #f65046;
  margin-top: 4px;
}

.timer-block-content {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 15px;
  line-height: 24px;
}

.timer-block-note {
  margin-right: 4px;
  @include mixin-text-main;
}

::v-deep input.time_input {
  width: 72px;
  padding: 8px 11px;
  font-size: 15px;
  line-height: 24px;
  letter-spacing: 0;
}

.sort_controls {
  display: flex;
  position: relative;
  justify-content: space-between;
  margin-bottom: 10px;
  .arrow {
    min-width: 40px;
    max-width: 40px;
    min-height: 20px;
    max-height: 20px;
    cursor: pointer;
    background: url('/static/images/elements/arrow_n.svg') 14px center no-repeat;
    &:hover {
      background-color: rgba(244, 245, 247, 0.56);
    }
    &.up {
      margin-bottom: 1px;
    }
    &.down {
      background-position: 13px center;
      transform: rotate(-180deg);
    }
  }
}

.question_title {
  font-weight: 500;
  font-size: 22px;
  line-height: 32px;
  color: #233346;
}

.other__add-type {
  cursor: pointer;
  position: relative;
  @include mixin-text-main;
  font-weight: 500;
  color: $grey;
  display: inline-block;
  vertical-align: top;
  transition: color 0.3s;

  &:hover {
    color: $green-hover;
    transition: color 0.3s;
  }
}

.options-dropdown {
  @include mixin-text-main;
  background: $white;
  position: relative;
  margin-left: auto;
}

.options-label {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 15px;
  border: 1px solid $grey-light;
  box-sizing: border-box;
  border-radius: 2px;
  height: 40px;
  min-width: 200px;
  cursor: pointer;
  transition: border 0.3s;

  img {
    transition: all ease-in-out 0.4s;
    transform: rotate(90deg);
  }

  &.is-active {
    border: 1px solid $green;
    transition: border 0.3s;

    img {
      transition: all ease-in-out 0.4s;
      transform: rotate(270deg);
    }
  }
}

.options-select {
  position: absolute;
  background: #fff;
  border: 1px solid $grey-light;
  box-sizing: border-box;
  border-radius: 2px;
  box-shadow: $box-shadow;
  right: 0;
  top: 40px;
  padding: 20px 15px;
  z-index: 2;
  transition: all ease-in-out 0.4s;

  ::v-deep {
    .vcheckbox_label,
    .checkbox_label {
      white-space: nowrap;
    }
  }
}

.checkbox_label {
  margin-left: 10px !important;
  line-height: 1.4 !important;
}

.options-select-option {
  margin-bottom: 10px;
  @include mixin-text-main;

  &:last-child {
    margin-bottom: 0;
  }
}

.row-block {
  align-self: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 0;
  margin-bottom: 12px;
}
.question .ql-snow.ql-toolbar button.ql-audio,
.question .ql-snow.ql-toolbar button.ql-audio-btn {
  width: 18px !important;
  height: 18px !important;
  position: relative;
  padding: 0 !important;
  display: inline-block;

  img {
    max-width: 100%;
  }
}
.question .ql-snow.ql-toolbar button.ql-image,
.question .ql-snow .ql-toolbar button.ql-image,
.ql-image {
  background: url('/static/icons/image-icon.png') 0 0 no-repeat !important;
  width: 22px !important;
  height: 16px !important;
  position: relative;
  padding: 0 !important;
  display: inline-block;

  svg {
    display: none;
  }
}

.medium-text {
  font-weight: 700;
}

.answers {
  &__trigger {
    margin-right: 10px;
  }

  &__type {
    &_choice {
      margin-top: 7px;

      .choice-answer {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        position: relative;

        &__delete {
          width: 15px;
          height: 15px;
          position: absolute;
          right: -30px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;

          &:before,
          &:after {
            position: absolute;
            top: 0;
            right: 50%;
            content: '';
            display: block;
            width: 2px;
            height: 18px;
            background: #d2d6e0;
          }

          &:before {
            transform: rotate(45deg);
          }

          &:after {
            transform: rotate(-45deg);
          }
        }

        .choice-input {
          width: 100%;
          margin-left: 17px;
          color: #9faab6;
        }

        .other {
          width: 100%;
          margin-left: 20px;
          padding: 10px;
          border-bottom: 1px solid #d2d6e0;
          color: #9faab6;
          display: flex;
          align-items: center;
          font-size: 15px;

          &.has-error {
            border-color: #f65046;
          }

          &__type {
            cursor: pointer;
            margin-right: 4px;
            font-weight: 500;
            position: relative;

            &:hover {
              color: #607182;
            }
          }

          &__add-type {
            position: relative;

            &:hover {
              color: #607182;
            }
          }

          span {
            font-weight: 500;
          }
        }
      }

      .answer {
        border: 2px solid #dadada;
      }
    }

    &_date {
      margin-top: 19px;
      position: relative;

      .date__range {
        width: 150px;
        padding: 10px 20px;
        border: 1px solid #d2d6e0;
        color: #9faab6;
      }

      ::v-deep {
        .vcheckbox_label .checkbox_label {
          margin-left: 10px;
        }
      }
    }

    &_file {
      margin-top: 12px;

      .file-title {
        font-size: 15px;
        line-height: 22px;
        color: #233346;
        display: block;
      }

      .file-wrapper {
        position: relative;
        min-width: 300px;
        margin-top: 4px;
        border: 1px solid #d2d6e0;
        padding: 11px 42px 11px 11px;
        display: inline-block;
        border-radius: 2px;
        color: #9faab6;
        font-size: 15px;

        .actions {
          position: absolute;
          top: 50%;
          right: 10px;
          transform: translateY(-50%);
        }
      }
    }
  }
}
</style>
